// src/pages/PrivacyPage.tsx
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const PrivacyPage: React.FC = () => {
  const { t } = useTranslation("legal");

  return (
    <div className="legal-root">
      <header className="lh">
        <h1>{t("privacy.title", "개인정보처리방침")}</h1>
        <Link className="back" to="/signal-intro">
          {t("back", "← 인트로로 돌아가기")}
        </Link>
      </header>

      {/* 수집 항목/방식 */}
      <section className="card">
        <h2>{t("privacy.collect", "수집하는 정보")}</h2>
        <p>
          {t(
            "privacy.collect.body",
            "본 앱은 회원가입/로그인 및 결제 기능을 제공하지 않으며, 이름·이메일·전화번호 등 직접 식별 가능한 정보를 서버에 수집/보관하지 않습니다. \
다만 광고 노출·부정 이용 방지·성능 개선을 위해 광고/분석 SDK가 단말 식별자(예: IDFA/AAID), 대략적 위치, 앱 버전/기기 정보, IP 주소 등을 수집할 수 있습니다. \
또한 기능 제공을 위해 로컬 저장소(localStorage, sessionStorage)에 사용자 설정(언어, 심볼, 차트 옵션, 광고 해금 시간 등)만 저장하며, 이는 사용자의 기기 안에만 보관됩니다."
          )}
        </p>
      </section>

      {/* 이용 목적 */}
      <section className="card">
        <h2>{t("privacy.purpose", "이용 목적")}</h2>
        <ul className="ul">
          <li>{t("privacy.purpose.ads", "광고 제공 및 보상형 해금 기능 동작")}</li>
          <li>{t("privacy.purpose.sec", "서비스 안정성 확보, 오류/부정행위 탐지")}</li>
          <li>{t("privacy.purpose.analytics", "집계 통계(비식별) 기반의 품질 개선")}</li>
          <li>{t("privacy.purpose.prefs", "사용자 로컬 환경설정 저장 및 복원")}</li>
        </ul>
      </section>

      {/* 제3자 제공(광고/SDK) */}
      <section className="card">
        <h2>{t("privacy.share", "제3자 제공")}</h2>
        <p>
          {t(
            "privacy.share.body",
            "본 앱은 개인을 직접 식별하는 정보를 판매하지 않습니다. 광고 노출, 부정 사용 방지, 통계 집계를 위해 광고 네트워크/분석/크래시 리포팅 파트너에 한해 위 기기 정보 등이 전송될 수 있습니다. \
각 파트너의 수집·이용·보관 정책은 해당사의 개인정보처리방침을 따릅니다."
          )}
        </p>
      </section>

      {/* 보관 기간 */}
      <section className="card">
        <h2>{t("privacy.retention", "보관 기간")}</h2>
        <p>
          {t(
            "privacy.retention.body",
            "앱 내 로컬 저장 정보는 사용자가 삭제하거나 앱을 제거할 때까지 기기 내에만 보관됩니다. 서버 로그 또는 파트너 보관 기간은 보안/운영 목적상 합리적인 최소기간(예: 최대 30일) 범위에서 유지될 수 있습니다."
          )}
        </p>
      </section>

      {/* 이용자 선택/권리 */}
      <section className="card">
        <h2>{t("privacy.rights", "이용자 선택 및 권리")}</h2>
        <ul className="ul">
          <li>
            {t(
              "privacy.rights.resetAds",
              "광고 식별자(AAID/IDFA) 재설정·추적 제한: 단말 설정에서 관리할 수 있습니다."
            )}
          </li>
          <li>
            {t(
              "privacy.rights.local",
              "로컬 저장 정보 삭제: 브라우저/앱에서 캐시·저장공간 삭제 또는 앱 재설치로 지울 수 있습니다."
            )}
          </li>
          <li>
            {t(
              "privacy.rights.contact",
              "그 밖의 문의는 아래 연락처로 요청해 주세요."
            )}
          </li>
        </ul>
      </section>

      {/* 아동 보호 */}
      <section className="card">
        <h2>{t("privacy.children", "아동의 개인정보 보호")}</h2>
        <p>
          {t(
            "privacy.children.body",
            "본 서비스는 만 13세(또는 거주 국가의 관련 기준) 미만 아동을 대상으로 하지 않으며, 아동으로부터 고의로 개인정보를 수집하지 않습니다."
          )}
        </p>
      </section>

      {/* 해외 이전/국가 */}
      <section className="card">
        <h2>{t("privacy.transfer", "국외 이전")}</h2>
        <p>
          {t(
            "privacy.transfer.body",
            "광고/분석 파트너의 서버는 여러 국가에 분산될 수 있으며, 서비스 이용 시 관련 국가로 데이터가 전송될 수 있습니다."
          )}
        </p>
      </section>

      {/* 변경 통지 */}
      <section className="card">
        <h2>{t("privacy.update", "정책 변경")}</h2>
        <p>
          {t(
            "privacy.update.body",
            "본 방침은 서비스/법규 변경에 따라 갱신될 수 있습니다. 중요한 변경 사항은 앱 내 공지 또는 웹사이트를 통해 안내합니다."
          )}
        </p>
        <p className="muted">{t("privacy.effective", "시행일")} : 2025-01-01</p>
      </section>

      {/* 연락처/링크 */}
      <footer className="lf">
        <a href="https://zuzusignal.com" target="_blank" rel="noreferrer">
          {t("privacy.contact", "문의·정보")} — zuzusignal.com
        </a>
        <span>·</span>
        <Link to="/signal-intro">{t("intro", "인트로")}</Link>
      </footer>

      <style>{`
        .legal-root{min-height:100dvh;background:#0b0f16;color:#eaeaea;padding:20px}
        .lh{display:flex;justify-content:space-between;align-items:center;gap:8px;max-width:960px;margin:0 auto 12px}
        .lh h1{font-size:20px;font-weight:900;margin:0}
        .back{color:#9ec1ff;text-decoration:none}
        .card{max-width:960px;margin:10px auto;border:1px solid #ffffff22;background:#111;border-radius:12px;padding:14px}
        .card h2{margin:0 0 6px;font-size:16px;font-weight:900}
        .card p{margin:0;color:#cbd5e1;line-height:1.6}
        .card .ul{margin:0;color:#cbd5e1;line-height:1.6;padding-left:18px}
        .card .ul li{margin:4px 0}
        .muted{color:#93a4bd;margin-top:6px}
        .lf{max-width:960px;margin:16px auto 0;display:flex;gap:10px;align-items:center;color:#bcd3ff}
        .lf a{color:#bcd3ff;text-decoration:none;border-bottom:1px solid transparent}
        .lf a:hover{border-bottom-color:#bcd3ff}
      `}</style>
    </div>
  );
};

export default PrivacyPage;
